import React, { useEffect, useState, useCallback, useRef } from 'react';
import './ProductTour.css';

const TOOLTIP_WIDTH = 320;
const TOOLTIP_MARGIN = 16;

/**
 * Tour de producto genérico y liviano (sin dependencia externa, alineado al
 * sistema de diseño Artemis — mismos tokens, gradiente y botones que el
 * resto de la app). Recibe una lista de pasos `{ selector, title, content }`
 * y muestra, por cada uno, un halo sobre el elemento del DOM que matchea
 * `selector` más una tarjeta con navegación Anterior/Siguiente/Saltar.
 *
 * Los pasos cuyo selector no exista en el DOM al iniciar el tour se omiten
 * silenciosamente — así un mismo recorrido sirve tanto si el usuario ya hizo
 * una selección (curso/sección) como si todavía no, sin tener que orquestar
 * distintos flujos según el estado de la página.
 */
function ProductTour({ steps, run, onClose, onFinish }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState([]);
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (!run) return;
    const found = steps.filter(s => document.querySelector(s.selector));
    setVisibleSteps(found);
    setStepIndex(0);
  }, [run, steps]);

  const current = visibleSteps[stepIndex];

  const measure = useCallback(() => {
    if (!current) { setRect(null); return; }
    const el = document.querySelector(current.selector);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [current]);

  useEffect(() => {
    if (!run || !current) { setRect(null); return undefined; }
    const el = document.querySelector(current.selector);
    if (el) {
      el.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });
    }
    // Espera a que termine el scroll suave antes de medir la posición final.
    const t = setTimeout(measure, prefersReducedMotion ? 0 : 280);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
    };
  }, [run, current, measure, prefersReducedMotion]);

  // Foco inicial en la tarjeta al abrir/cambiar de paso, para navegación por teclado.
  useEffect(() => {
    if (run && current && tooltipRef.current) {
      tooltipRef.current.focus();
    }
  }, [run, current]);

  const isLast = visibleSteps.length > 0 && stepIndex >= visibleSteps.length - 1;

  const handleNext = useCallback(() => {
    if (isLast) {
      onFinish && onFinish();
      return;
    }
    setStepIndex(i => i + 1);
  }, [isLast, onFinish]);

  const handlePrev = useCallback(() => setStepIndex(i => Math.max(0, i - 1)), []);
  const handleSkip = useCallback(() => onClose && onClose(), [onClose]);

  useEffect(() => {
    if (!run || !current) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); handleSkip(); }
      else if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); handleNext(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); handlePrev(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [run, current, handleNext, handlePrev, handleSkip]);

  if (!run || !current) return null;

  const PADDING = 8;
  const viewportWidth = window.innerWidth;
  const tooltipWidth = Math.min(TOOLTIP_WIDTH, viewportWidth - TOOLTIP_MARGIN * 2);

  const spotlightStyle = rect ? {
    top: rect.top - PADDING,
    left: rect.left - PADDING,
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
  } : null;

  const tooltipStyle = rect ? (() => {
    const fitsBelow = rect.bottom + 220 < window.innerHeight;
    const left = Math.min(
      Math.max(rect.left, TOOLTIP_MARGIN),
      Math.max(TOOLTIP_MARGIN, viewportWidth - tooltipWidth - TOOLTIP_MARGIN)
    );
    return {
      left,
      top: fitsBelow ? rect.bottom + 16 : Math.max(TOOLTIP_MARGIN, rect.top - 210),
      width: tooltipWidth,
    };
  })() : {
    left: (viewportWidth - tooltipWidth) / 2,
    top: '38%',
    width: tooltipWidth,
  };

  return (
    <>
      <div className="product-tour-overlay" onClick={handleSkip} />
      {spotlightStyle && (
        <div
          className={`product-tour-spotlight${prefersReducedMotion ? ' product-tour-no-motion' : ''}`}
          style={spotlightStyle}
        />
      )}
      <div
        className={`product-tour-tooltip${prefersReducedMotion ? ' product-tour-no-motion' : ''}`}
        style={tooltipStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-tour-title"
        aria-describedby="product-tour-body"
        tabIndex={-1}
        ref={tooltipRef}
      >
        <div className="product-tour-header">
          <strong id="product-tour-title">{current.title}</strong>
          <button type="button" className="product-tour-close" onClick={handleSkip} aria-label="Cerrar tour">
            ×
          </button>
        </div>
        <div className="product-tour-body" id="product-tour-body">{current.content}</div>
        <div className="product-tour-footer">
          <div className="product-tour-dots" role="img" aria-label={`Paso ${stepIndex + 1} de ${visibleSteps.length}`}>
            {visibleSteps.map((s, i) => (
              <span
                key={s.selector}
                className={`product-tour-dot${i === stepIndex ? ' is-active' : ''}`}
                aria-hidden="true"
              />
            ))}
          </div>
          <div className="d-flex gap-2">
            {stepIndex > 0 && (
              <button type="button" className="btn-artemis btn-artemis-sm btn-artemis-ghost" onClick={handlePrev}>
                Anterior
              </button>
            )}
            <button type="button" className="btn-artemis btn-artemis-sm btn-artemis-primary" onClick={handleNext}>
              {isLast ? 'Finalizar' : 'Siguiente'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default ProductTour;
